import { useId } from "react";
import MoviePoster from "./MoviePoster";
import MoviePosterPreview from "./MoviePosterPreview";
import catalogMetadata from "../data/catalogPosterMetadata.json";
import pilotMovies from "../data/pilot_support20_onboarding.json";

const catalogById = new Map(pilotMovies.map(movie => [Number(movie.movieId), movie]));

export default function SelectedMovies({ movies, system, onRemove, renderRating }) {
  const headingId = useId();
  const isGers = system === "GERS";

  return (
    <>
      <section aria-labelledby={headingId}>
        <div className="mb-3 flex items-center gap-3">
          <h2 id={headingId} className="text-lg font-semibold">Selected movies</h2>
          <span
            aria-label={`${movies.length} selected movies`}
            className={`rounded-full px-2.5 py-0.5 text-sm font-semibold tabular-nums ${
              isGers ? "bg-green-400/15 text-green-300" : "bg-[#00C8FF]/15 text-[#A7E8FF]"
            }`}
          >
            {movies.length}
          </span>
        </div>

        {movies.length === 0 ? (
          <p className="rounded-xl border border-dashed border-white/20 bg-white/5 p-4 text-sm text-gray-400">
            No movies selected yet. Choose movies from the catalog below.
          </p>
        ) : (
          <>
            {renderRating && <p className="mb-3 text-sm text-gray-300">Rate selected movies:</p>}
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {movies.map((movie) => {
                // Older saved GERS selections contain genre IDs only.
                const genres = movie.genres || catalogById.get(Number(movie.movieId))?.genres || [];
                const cached = catalogMetadata[movie.movieId];
                const overview = movie.overview || (cached?.canonical_title === movie.title ? cached.overview : "") || "Synopsis unavailable.";
                return (
                <li
                  key={movie.movieId}
                  data-selected-movie-id={movie.movieId}
                  className={`relative grid grid-cols-[3rem_minmax(0,1fr)] items-start gap-3 rounded-xl border bg-white/5 p-3 ${
                    isGers ? "border-green-400/40" : "border-[#00C8FF]/40"
                  }`}
                >
                  <MoviePosterPreview movieId={movie.movieId} title={movie.title}
                    urls={[movie.poster, ...(movie.poster_urls || [])]} overview={movie.overview}
                    tmdbId={movie.tmdbId} imdbId={movie.imdbId} year={movie.year} rating={movie.rating} genres={genres}>
                    <button type="button" aria-label={`Preview ${movie.title}`} className="rounded-lg">
                      <span aria-hidden="true">
                        <MoviePoster movieId={movie.movieId} title={movie.title}
                          urls={[movie.poster, ...(movie.poster_urls || [])]} compact />
                      </span>
                    </button>
                  </MoviePosterPreview>
                  <div className="min-w-0">
                    <p className="pr-7 text-sm font-medium leading-snug break-words">{movie.title}</p>
                    {genres.length > 0 && (
                      <p className={`mt-1 text-xs leading-relaxed ${isGers ? "text-green-300" : "text-[#A7E8FF]"}`}>
                        {genres.join(" · ")}
                      </p>
                    )}
                    <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-gray-300">
                      {overview}
                    </p>
                    {renderRating && <div className="mt-2">{renderRating(movie)}</div>}
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${movie.title} from selected movies`}
                    onClick={() => onRemove(movie)}
                    className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-xl text-gray-300 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
                );
              })}
            </ul>
          </>
        )}
      </section>
      <hr className={`my-6 border-t-2 ${isGers ? "border-green-400/50" : "border-[#00C8FF]/50"}`} />
    </>
  );
}
